import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import "./MultiSearchableCombobox.css";

interface MultiSearchableComboboxProps {
  items: { value: number | string; label: string }[];
  values: (number | string)[];
  placeholder?: string;
  onChange: (values: (number | string)[]) => void;
  accentColor?: string;
}

export const MultiSearchableCombobox: React.FC<MultiSearchableComboboxProps> = ({
  items,
  values,
  placeholder = "Sélectionner...",
  onChange,
  accentColor = "#6366f1",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedItems = items.filter((i) => values.includes(i.value));
  const MAX_VISIBLE_TAGS = 2;
  const visibleTags = selectedItems.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = selectedItems.length - MAX_VISIBLE_TAGS;

  const filtered =
    query.length === 0
      ? items
      : items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()));

  const toggleItem = (v: string | number) => {
    if (values.includes(v)) {
      onChange(values.filter((val) => val !== v));
    } else {
      onChange([...values, v]);
    }
  };

  const removeItem = (v: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(values.filter((val) => val !== v));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
    setQuery("");
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (ref.current && !ref.current.contains(document.activeElement)) {
        setOpen(false);
        setQuery("");
      }
    }, 150);
  };

  const updatePanelPosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const panelHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < 150 && rect.top > panelHeight;
    setPanelStyle({
      position: "fixed",
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  };

  useEffect(() => {
    if (open) updatePanelPosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePanelPosition();
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open]);

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = ref.current?.contains(target);
      const insidePanel = panelRef.current?.contains(target);
      if (!insideTrigger && !insidePanel) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  return (
    <div className="mcombo" ref={ref}>
      <div
        className={`mcombo-trigger ${open ? "mcombo-trigger--open" : ""}`}
        style={{
          borderColor: open ? accentColor : undefined,
          boxShadow: open ? `0 0 0 2px ${accentColor}20` : undefined,
        }}
        onClick={() => {
          if (!open) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <div className="mcombo-tags">
          {selectedItems.length === 0 && !open && (
            <span className="mcombo-placeholder">{placeholder}</span>
          )}
          {visibleTags.map((item) => (
            <span
              key={item.value}
              className="mcombo-tag"
              style={{
                backgroundColor: `${accentColor}15`,
                color: accentColor,
                borderColor: `${accentColor}30`,
              }}
            >
              <span className="mcombo-tag-text">{item.label}</span>
              <X
                size={14}
                className="mcombo-tag-remove"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => removeItem(item.value, e)}
              />
            </span>
          ))}
          {hiddenCount > 0 && (
            <span
              className="mcombo-tag mcombo-tag--count"
              style={{
                backgroundColor: `${accentColor}15`,
                color: accentColor,
                borderColor: `${accentColor}30`,
              }}
            >
              +{hiddenCount}
            </span>
          )}
          <input
            ref={inputRef}
            className="mcombo-input"
            placeholder={selectedItems.length > 0 ? "" : open ? placeholder : ""}
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <ChevronDown
          size={18}
          className={`mcombo-chevron ${open ? "mcombo-chevron--open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (open) {
              setOpen(false);
              setQuery("");
            } else {
              setOpen(true);
              inputRef.current?.focus();
            }
          }}
        />
      </div>

      {open && createPortal(
        <div className="mcombo-panel" style={panelStyle} ref={panelRef}>
          {values.length > 0 && (
            <div
              className="mcombo-clear"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange([])}
            >
              Tout effacer
            </div>
          )}
          <div className="mcombo-list">
            {filtered.length === 0 && <div className="mcombo-empty">Aucun résultat</div>}
            {filtered.map((item) => {
              const isSelected = values.includes(item.value);
              return (
                <div
                  key={item.value}
                  className={`mcombo-item ${isSelected ? "mcombo-item--selected" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleItem(item.value)}
                >
                  <div
                    className="mcombo-checkbox"
                    style={{
                      borderColor: isSelected ? accentColor : "#cbd5e1",
                      backgroundColor: isSelected ? accentColor : "transparent",
                    }}
                  >
                    {isSelected && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MultiSearchableCombobox;
